import { Router } from "@angular/router";
import { map, tap } from "rxjs";
import { inject, Injectable } from "@angular/core";

import { UserRole } from "../../enums";
import { HttpService } from "../http/http.service";
import { AuthService } from "../auth/auth.service";
import { GetPagedBody, ReservationPayload, ReservationResponse } from "../../interfaces";

@Injectable({
  providedIn: "root",
})
export class ReservationService extends HttpService {
  private router = inject(Router);
  private authService = inject(AuthService);

  protected override get baseUrl(): string {
    return "reservation/";
  }

  readonly isGuest = this.authService.isGuest;

  get hasAdminAccess() {
    return [UserRole.ADMIN, UserRole.COUNTER, UserRole.SUPERADMIN, UserRole.VISITORSERVICE].includes(this.authService.userRole()!);
  }

  addReservation(body: Record<string, unknown>) {
    return this.post({ APIName: "add-reservation", body }).pipe(
      tap((reservationId) => {
        if (reservationId) localStorage.setItem("requestId", btoa(reservationId.toString()));

        if (this.isGuest()) {
          this.router.navigate([`/ticket/${reservationId}`]);
          return;
        }

        if (this.hasAdminAccess) {
          this.router.navigate([`/admin/reservations/view/${reservationId}`]);
          return;
        }

        this.router.navigate([`/my-tickets/${reservationId}`]);
      }),
    );
  }

  add(body: Record<string, unknown>) {
    return this.post({ APIName: "add", body });
  }

  addReservationAttachment(body: Record<string, unknown>) {
    return this.post({ APIName: "add-reservation-attachment", body });
  }

  getMyReservations(body: GetPagedBody<Partial<ReservationPayload>>) {
    const filter = body.filter || {};
    body.filter = { ...filter, allowAll: filter.allowAll || false };

    return this.getPagedRequest<ReservationResponse[]>({ APIName: "my-reservation", body });
  }

  getAllReservations(body: Partial<ReservationPayload>) {
    return this.getPagedRequest<ReservationResponse[]>({ APIName: "all-reservation", body });
  }

  getReservation(reservationNumber: string | number) {
    return this.get<ReservationResponse>({ APIName: `reservation/${reservationNumber}` }).pipe(
      map((reservation) => this.#constructReservationResponse(reservation)),
    );
  }

  getReservationByTicketNumber(ticketNumber: string | number) {
    return this.get<ReservationResponse>({ APIName: `ticket/${ticketNumber}` }).pipe(
      map((reservation) => this.#constructReservationResponse(reservation)),
    );
  }

  #constructReservationResponse(reservation: ReservationResponse) {
    return reservation;
  }

  getReservedSeats(eventId: number) {
    return this.get({ APIName: `GetReservedSeats/${eventId}` });
  }

  getHallReservedDays(body: Record<string, unknown>) {
    return this.post({ APIName: "GetHallReservedDays", body });
  }

  reIssue(body: Record<string, unknown>) {
    return this.post({ APIName: "reissue-reservation", body, showAlert: true });
  }

  confrimReservation(reservationId: string | number) {
    return this.get({ APIName: `ConfirmHallReservation/${reservationId}` });
  }

  download(ticketNumber: string | number) {
    return this.get<Blob>({ APIName: `getreservationpdf/${ticketNumber}`, responseType: "blob" as "json" });
  }

  print(ticketNumber: string | number) {
    return this.get({ APIName: `print/${ticketNumber}` });
  }

  updateReservationStatus(body: Record<string, unknown>) {
    return this.put({ APIName: "update-reservation-status", body });
  }

  refund(reservationId: string | number) {
    return this.put({ APIName: `reservation-refund`, body: { reservationId } });
  }

  removeReservation(reservationId: string | number) {
    return this.delete({ APIName: `deletesoft/${reservationId}` });
  }

  updateReservationsStatus(body: any) {
    return this.put({ APIName: "update-reservations-status", body });
  }

  delayReservation(reservationId: string | number) {
    return this.put({ APIName: `delay-reservation/${reservationId}` });
  }
}
